<template>
  <div
    class="p-2 sm:p-3 rounded-md flex flex-col gap-1.5"
    :class="itemBackgroundClass"
  >
    <!-- Item Info -->
    <div class="flex items-center flex-wrap gap-1.5">
      <span class="font-bold text-xl sm:text-2xl text-gray-900 dark:text-white">{{ item.quantity }}x</span>
      <span class="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{{ item.menu_item.name }}</span>
      <span
        v-if="item.variant"
        class="text-sm sm:text-base text-gray-600 dark:text-gray-400"
      >({{ item.variant.name
      }})</span>

      <span
        class="inline-flex items-center rounded px-1 py-0.5 text-xs font-normal italic opacity-90"
        :class="getItemStatusBadgeClass(item.status)"
      >
        {{ t(`app.status.${item.status}`) }}
      </span>
    </div>

    <!-- Category and Action Button -->
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-1.5 flex-wrap">
        <span
          v-if="item.menu_item.category"
          class="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase"
        >
          {{ item.menu_item.category }}
        </span>
        
        <!-- Item Type Badge (only for takeaway items) -->
        <span
          v-if="item.item_type === 'takeaway'"
          class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
        >
          PARA LLEVAR
        </span>
      </div>

      <!-- Action Buttons -->
      <div v-if="showButtons">
        <BaseButton
          v-if="item.status === 'pending'"
          variant="primary"
          size="xs"
          outlined
          class="flex-shrink-0"
          @click="$emit('start-preparing', item)"
        >
          {{ t('app.views.kitchen.actions.start_preparing') }}
        </BaseButton>
        <BaseButton
          v-if="item.status === 'preparing'"
          variant="success"
          size="xs"
          outlined
          class="flex-shrink-0"
          @click="$emit('mark-ready', item)"
        >
          {{ t('app.views.kitchen.actions.item_ready') }}
        </BaseButton>
      </div>
    </div>

    <!-- Special Instructions -->
    <div
      v-if="item.special_instructions"
      class="p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm sm:text-base font-semibold text-blue-700 dark:text-blue-300"
    >
      {{ item.special_instructions }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { OrderItem } from '@/services/orderService';
import { getItemStatusBadgeClass } from '@/utils/kitchenHelpers';
import BaseButton from '@/components/ui/BaseButton.vue';

interface Props {
  item: OrderItem;
  showButtons?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showButtons: true
});

defineEmits<{
  'start-preparing': [item: OrderItem];
  'mark-ready': [item: OrderItem];
}>();

const { t } = useI18n();

const itemBackgroundClass = computed(() => {
  return {
    'bg-yellow-50 dark:bg-yellow-900/20': props.item.status === 'pending',
    'bg-blue-50 dark:bg-blue-900/20': props.item.status === 'preparing',
    'bg-green-50 dark:bg-green-900/20': props.item.status === 'ready'
  };
});
</script>
